import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts";
import type { RangeValue } from "@/shared/ui";
import { selectFinanceScope } from "@/features/settings";
import { useGetProjectsQuery, useGetMyProjectsQuery } from "@/features/projects";
import { useGetCashFlowQuery } from "@/features/accounting/financial-reports/api";
import { useGetBudgetSummaryQuery } from "../api";
import { getCurrentYear, buildYearRange, formatISO, computeInterval , parseMoney } from "../utils";
import { getLocalizedText } from "../utils/localization";
import { formatChartLabel } from "../utils/chartLabels";
import type { BudgetSection, BudgetExecutionRow } from "../types";
import { useProfitLossData } from "./useProfitLossData";
import { useCashFlowData } from "./useCashFlowData";

export function useFinancePage() {
  const { t, i18n } = useTranslation("FinancePage");
  const locale = (i18n.language || "ru") as "ru" | "kk" | "en";

  const { hasPermission } = useAuth();
  const canEditProject = hasPermission(PERMISSIONS.PROJECTS.EDIT_PROJECT);
  const financeScope = useAppSelector(selectFinanceScope);
  const activeProjectId = typeof financeScope === "number" ? financeScope : null;
  const allProjects = useGetProjectsQuery(undefined, { skip: !canEditProject });
  const myProjects = useGetMyProjectsQuery(undefined, { skip: canEditProject });
  const { data: projects } = canEditProject ? allProjects : myProjects;
  const activeProject = typeof financeScope === "number" ? projects?.find((p) => p.id === financeScope) : undefined;
  const activeProjectName = activeProject?.name ?? null;

  const scopeParam: "all" | "internal" | "external" | undefined =
    financeScope === "internal"
      ? "internal"
      : financeScope === "external"
        ? "external"
        : financeScope === null
          ? "all"
          : undefined;

  const now = useMemo(() => new Date(), []);
  const [dateRange, setDateRange] = useState<RangeValue>({
    start: new Date(now.getFullYear(), 0, 1),
    end: new Date(now.getFullYear(), 11, 31),
  });

  const startDateStr = dateRange.start ? formatISO(dateRange.start) : "";
  const endDateStr = dateRange.end ? formatISO(dateRange.end) : "";
  const interval = startDateStr && endDateStr ? computeInterval(startDateStr, endDateStr) : undefined;
  const canFetchTimeSeries = startDateStr !== "" && endDateStr !== "" && interval != null;

  const fullYearRange = useMemo(() => buildYearRange(getCurrentYear()), []);

  const {
    data: budgetData,
    isLoading: isBudgetLoading,
    isFetching: isBudgetFetching,
    error: budgetError,
    refetch: refetchBudget,
  } = useGetBudgetSummaryQuery({
    start_date: startDateStr || fullYearRange.start_date,
    end_date: endDateStr || fullYearRange.end_date,
    project_id: activeProjectId ?? undefined,
    scope: scopeParam,
    include_time_series: false,
  });

  const balance = budgetData?.balance ?? null;
  const balanceMode = balance?.mode ?? null;
  const hidePlanned = balanceMode !== "planned";

  const {
    data: timeSeriesBudgetData,
    isLoading: isTimeSeriesBudgetLoading,
  } = useGetBudgetSummaryQuery(
    {
      start_date: startDateStr,
      end_date: endDateStr,
      project_id: activeProjectId ?? undefined,
      scope: scopeParam,
      include_time_series: true,
      interval,
    },
    { skip: !canFetchTimeSeries },
  );

  const budgetTimeSeries = timeSeriesBudgetData?.time_series ?? null;

  const profitLossData = useProfitLossData({
    budgetTimeSeries,
    startDate: startDateStr,
    endDate: endDateStr,
    interval,
    projectId: activeProjectId,
    locale,
  });

  const cashFlowData = useCashFlowData({
    startDate: startDateStr,
    endDate: endDateStr,
    interval: interval ?? undefined,
    projectId: activeProjectId,
    locale,
  });

  const {
    data: cashFlowAggregate,
  } = useGetCashFlowQuery(
    { start_date: startDateStr, end_date: endDateStr, project_id: activeProjectId ?? undefined },
    { skip: !startDateStr || !endDateStr },
  );

  const netCashFlow = cashFlowAggregate ? parseMoney(cashFlowAggregate.net_cash_change) : null;

  const budgetExecutionData = useMemo<BudgetExecutionRow[]>(() => {
    if (!budgetData?.sections?.length) return [];
    return budgetData.sections.map(function buildRow(section) {
      return {
        sectionId: section.id,
        sectionName: getLocalizedText(section.name, locale),
        planned: parseMoney(section.planned_total),
        actual: parseMoney(section.total),
        executionPct: section.execution_percentage,
      };
    });
  }, [budgetData, locale]);

  const expenseBreakdownData = useMemo(() => {
    if (!budgetTimeSeries?.points?.length) return [];
    return budgetTimeSeries.points.map(function buildPoint(point) {
      const row: Record<string, string | number> = {
        name: formatChartLabel(point.date, budgetTimeSeries.interval, locale),
      };
      for (const [key, value] of Object.entries(point.subcategories)) {
        row[key] = parseMoney(value ?? "0");
      }
      return row;
    });
  }, [budgetTimeSeries, locale]);

  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  const activeSection = useMemo(() => {
    if (!budgetData?.sections?.length) return null;
    if (activeSectionId) {
      const found = budgetData.sections.find((s) => s.id === activeSectionId);
      if (found) return found;
    }
    return budgetData.sections[0];
  }, [budgetData, activeSectionId]);

  function handleSectionClick(section: BudgetSection) {
    setActiveSectionId(section.id);
  }

  return {
    t,
    locale,
    activeProjectName,
    activeProjectId,
    financeScope,
    balance,
    balanceMode,
    hidePlanned,
    budgetData: budgetData ?? null,
    isBudgetLoading,
    isBudgetFetching,
    budgetError,
    refetchBudget,
    activeSection,
    handleSectionClick,
    dateRange,
    setDateRange,
    profitLossData,
    cashFlowData,
    netCashFlow,
    budgetExecutionData,
    expenseBreakdownData,
    isTimeSeriesBudgetLoading,
    budgetTimeSeries,
  };
}
